import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AdminSettings, AgentCustomRule, AgentModule, ModuleID, PermissionSet, Role } from '../types';
import { backupApi, BackupEntry, BackupSettings } from '../services/backupApi';
import UserManager from './UserManager';
import { PermissionsTab } from './AdminPortal/PermissionsTab';
import { adminService } from '../services/adminService';
import { AgentTab } from './AdminPortal/AgentTab';
import { AITab } from './AdminPortal/AITab';
import { validateAgentTaskRouting } from '../utils/aiRoutingValidation';
import { LoginPageTab } from './AdminPortal/LoginPageTab';
import { TourTab } from './AdminPortal/TourTab';
import { SPhoneTab } from './AdminPortal/SPhoneTab';
import { InboundBridgeTab } from './AdminPortal/InboundBridgeTab';
import { InboundReplyTemplatesTab } from './AdminPortal/InboundReplyTemplatesTab';
import { GDashTab } from './AdminPortal/GDashTab';
import { GoogleContactsTab } from './AdminPortal/GoogleContactsTab';
import { userService } from '../services/userService';
import { whatsappService, WhatsAppNumberItem, WhatsAppSessionItem } from '../services/whatsappService';

type AdminToolsProps = {
  settings: AdminSettings;
  setSettings: (s: AdminSettings) => void;
};

const formatBytes = (value: number) => {
  if (!value) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let idx = 0;
  let size = value;
  while (size >= 1024 && idx < units.length - 1) {
    size /= 1024;
    idx += 1;
  }
  return `${size.toFixed(2)} ${units[idx]}`;
};

const AdminTools: React.FC<AdminToolsProps> = ({ settings, setSettings }) => {
  const [backupSettings, setBackupSettings] = React.useState<BackupSettings | null>(settings.backup || null);
  const [history, setHistory] = React.useState<BackupEntry[]>([]);
  const [status, setStatus] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [restoreId, setRestoreId] = React.useState('');
  const [restoreConfirm, setRestoreConfirm] = React.useState('');
  const [remoteStatus, setRemoteStatus] = React.useState<{ ok: boolean; message: string } | null>(null);
  const [remoteCheckedAt, setRemoteCheckedAt] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<'backup' | 'users' | 'permissions' | 'agent' | 'ai' | 'loginpage' | 'whatsapp' | 'googlecontacts' | 'sphone' | 'tours' | 'inbound' | 'monitor' | 'commsmonitor' | 'other'>('backup');
  const [inboundView, setInboundView] = React.useState<'bridge' | 'reply-templates' | 'gdash'>('bridge');
  const [permSaveStatus, setPermSaveStatus] = React.useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [permSaving, setPermSaving] = React.useState(false);
  const [activeAgentModule, setActiveAgentModule] = React.useState<AgentModule>('QUOTER');
  const [customRulesJsonDraft, setCustomRulesJsonDraft] = React.useState<string>('[]');
  const [customRulesJsonError, setCustomRulesJsonError] = React.useState<string | null>(null);
  const [customRuleDraft, setCustomRuleDraft] = React.useState<AgentCustomRule>({
    id: '',
    title: '',
    severity: 'MEDIUM',
    detail: '',
    suggestion: '',
    enabled: true,
    logic: 'all',
    conditions: [{ field: '', op: 'eq', value: '' }],
  });
  const [ruleTestResult, setRuleTestResult] = React.useState<string>('');
  const [agentHistory, setAgentHistory] = React.useState<any[]>([]);
  const [waSessions, setWaSessions] = React.useState<WhatsAppSessionItem[]>([]);
  const [waNumbers, setWaNumbers] = React.useState<WhatsAppNumberItem[]>([]);
  const [waOwners, setWaOwners] = React.useState<Array<{ id: string; name: string; email?: string | null }>>([]);
  const [waNumberId, setWaNumberId] = React.useState('');
  const [waQrCode, setWaQrCode] = React.useState<string | null>(null);
  const [waMediaStatus, setWaMediaStatus] = React.useState<{ pending: number; failed: number; inFlight: number; completed: number; processing: number; byOperator?: any[] }>({ pending: 0, failed: 0, inFlight: 0, completed: 0, processing: 0 });
  const [waBackfillLimit, setWaBackfillLimit] = React.useState('25');
  const [waBackfillIncludeFailed, setWaBackfillIncludeFailed] = React.useState(true);
  const [waBackfillRunning, setWaBackfillRunning] = React.useState(false);
  const [waBackfillProgress, setWaBackfillProgress] = React.useState<{ processed: number; total: number } | null>(null);
  const waBackfillPollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const defaultWaTransferSettings = React.useMemo(() => ({
    enabled: true,
    popupEnabled: true,
    autoIntroEnabled: true,
    allowOperatorOverride: true,
    autoIntroTemplate: "Hi, I'm taking over your request now. I will assist you from here.",
    returnToOwnerTemplate: "Welcome back {{clientName}}, I am now back on your chat and will continue assisting you.",
    askFeedbackBeforeReturn: false,
    feedbackPromptTemplate: "Before I return you to my colleague, was this support helpful? (Yes/No)",
  }), []);
  const [waTransferSettings, setWaTransferSettings] = React.useState({
    ...defaultWaTransferSettings,
    ...(settings.whatsappTransfer || {}),
  });
  const [waTransferSaving, setWaTransferSaving] = React.useState(false);
  const defaultWaRecallSettings = React.useMemo(() => ({
    enabled: true,
    maxRecallMinutes: 120,
    allowLeadershipOverride: true,
    requireReason: false,
    allowMediaRecall: true,
  }), []);
  const [waRecallSettings, setWaRecallSettings] = React.useState({
    ...defaultWaRecallSettings,
    ...(settings.whatsappRecall || {}),
  });
  const [waRecallSaving, setWaRecallSaving] = React.useState(false);
  const [monitorAccess, setMonitorAccess] = React.useState<{
    hasToken: boolean;
    active: boolean;
    alias?: string | null;
    createdAt?: string | null;
    expiresAt?: string | null;
    revokedAt?: string | null;
    lastGeneratedBy?: string | null;
  } | null>(null);
  const [monitorExpiryDays, setMonitorExpiryDays] = React.useState('30');
  const [monitorAlias, setMonitorAlias] = React.useState('');
  const [monitorShortLink, setMonitorShortLink] = React.useState('');
  const [monitorFullLink, setMonitorFullLink] = React.useState('');
  const [monitorShowQr, setMonitorShowQr] = React.useState(false);
  const [monitorBusy, setMonitorBusy] = React.useState(false);
  const [commsMonitorAccess, setCommsMonitorAccess] = React.useState<{
    hasToken: boolean;
    active: boolean;
    alias?: string | null;
    createdAt?: string | null;
    expiresAt?: string | null;
    revokedAt?: string | null;
    lastGeneratedBy?: string | null;
  } | null>(null);
  const [commsMonitorExpiryDays, setCommsMonitorExpiryDays] = React.useState('30');
  const [commsMonitorAlias, setCommsMonitorAlias] = React.useState('reservation-wall');
  const [commsMonitorShortLink, setCommsMonitorShortLink] = React.useState('');
  const [commsMonitorFullLink, setCommsMonitorFullLink] = React.useState('');
  const [commsMonitorShowQr, setCommsMonitorShowQr] = React.useState(false);
  const [commsMonitorBusy, setCommsMonitorBusy] = React.useState(false);
  const [waEditingId, setWaEditingId] = React.useState('');
  const [waForm, setWaForm] = React.useState({
    ownerUserId: '',
    phoneNumber: '',
    jid: '',
    label: '',
    sessionKey: '',
    status: 'PAUSED' as 'ACTIVE' | 'PAUSED' | 'DISCONNECTED' | 'NEEDS_RELINK',
    automation: {
      transferAfterMinutes: '',
      transferToUserId: '',
      notifyAfterMinutes: '',
      notifyUserId: '',
    },
  });
  const selectedWaNumber = React.useMemo(
    () => waNumbers.find(item => item.id === waNumberId.trim()) || null,
    [waNumbers, waNumberId]
  );
  const formatDateTime = React.useCallback((value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  }, []);

  // Auto-refresh the QR code every 15 seconds while it is displayed.
  // Baileys rotates the QR roughly every 20s; scanning a stale code gives "Couldn't link device".
  React.useEffect(() => {
    if (!waQrCode || !waNumberId.trim()) return;
    const id = waNumberId.trim();
    const timer = setInterval(async () => {
      try {
        const result = await whatsappService.getQrCode(id);
        if (result.qrCode) {
          setWaQrCode(result.qrCode);
        } else {
          // Number connected or session ended — stop polling
          setWaQrCode(null);
          clearInterval(timer);
        }
      } catch {
        // Silently ignore — user can manually refresh
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [!!waQrCode, waNumberId]);

  const load = async () => {
    setIsLoading(true);
    try {
      const [cfg, list, waSessionResult, waNumbersResult, waMediaResult, userList] = await Promise.all([
        backupApi.getSettings(),
        backupApi.listHistory(),
        whatsappService.getSessions().catch(() => ({ items: [] })),
        whatsappService.listNumbers().catch(() => ({ items: [] })),
        whatsappService.getMediaProcessingStatus().catch(() => ({ pending: 0, failed: 0, inFlight: 0 })),
        userService.getAllUsers().catch(() => []),
      ]);
      setBackupSettings(cfg);
      setHistory(list);
      setWaSessions(Array.isArray(waSessionResult.items) ? waSessionResult.items : []);
      setWaNumbers(Array.isArray(waNumbersResult.items) ? waNumbersResult.items : []);
      setWaMediaStatus({
        pending: (waMediaResult as any).pending ?? 0,
        failed: (waMediaResult as any).failed ?? 0,
        inFlight: (waMediaResult as any).inFlight ?? 0,
        completed: (waMediaResult as any).completed ?? 0,
        processing: (waMediaResult as any).processing ?? 0,
        byOperator: (waMediaResult as any).byOperator,
      });
      setWaOwners(
        (Array.isArray(userList) ? userList : []).map((user: any) => ({
          id: String(user.id || ''),
          name: String(user.name || user.email || user.id || ''),
          email: user.email || null,
        }))
      );
      setStatus(null);
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to load backup settings.');
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  React.useEffect(() => {
    setWaTransferSettings({
      ...defaultWaTransferSettings,
      ...(settings.whatsappTransfer || {}),
    });
  }, [defaultWaTransferSettings, settings.whatsappTransfer]);

  React.useEffect(() => {
    setWaRecallSettings({
      ...defaultWaRecallSettings,
      ...(settings.whatsappRecall || {}),
    });
  }, [defaultWaRecallSettings, settings.whatsappRecall]);

  const refreshWhatsAppMediaStatus = async () => {
    try {
      const next = await whatsappService.getMediaProcessingStatus();
      setWaMediaStatus({
        pending: next.pending ?? 0,
        failed: next.failed ?? 0,
        inFlight: next.inFlight ?? 0,
        completed: next.completed ?? 0,
        processing: next.processing ?? 0,
        byOperator: next.byOperator,
      });
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to load WhatsApp media processing status.');
    }
  };

  const runWhatsAppMediaBackfill = async () => {
    if (waBackfillRunning) return;
    setWaBackfillRunning(true);
    setWaBackfillProgress(null);
    try {
      const limit = Math.max(1, Math.min(500, Number(waBackfillLimit || 25)));
      // Always fire as a background job — synchronous awaiting of media downloads
      // causes 504 Gateway Timeout for any batch larger than a handful of items.
      await whatsappService.backfillMediaProcessing({
        limit,
        includeFailed: waBackfillIncludeFailed,
        runAll: true,
      });
      setStatus(`Background backfill started (up to ${limit} items)...`);

      if (waBackfillPollRef.current) clearInterval(waBackfillPollRef.current);
      waBackfillPollRef.current = setInterval(async () => {
        try {
          const prog = await whatsappService.getBackfillProgress();
          setWaBackfillProgress({ processed: prog.progress?.processed ?? 0, total: prog.progress?.total ?? 0 });
          setWaMediaStatus({
            pending: prog.pending ?? 0,
            failed: prog.failed ?? 0,
            inFlight: prog.inFlight ?? 0,
            completed: prog.completed ?? 0,
            processing: prog.processing ?? 0,
            byOperator: prog.byOperator,
          });
          if (!prog.progress?.running) {
            clearInterval(waBackfillPollRef.current!);
            waBackfillPollRef.current = null;
            setWaBackfillRunning(false);
            setStatus(`Backfill complete — ${prog.progress?.processed ?? 0} item(s) processed. Completed: ${prog.completed ?? 0}, Failed: ${prog.failed ?? 0}.`);
          }
        } catch {
          // ignore poll errors, keep polling
        }
      }, 4000);
    } catch (error: any) {
      setWaBackfillRunning(false);
      setStatus(error?.response?.data?.error || error?.message || 'Failed to backfill WhatsApp media.');
    }
  };

  const runWhatsAppMediaBackfillAll = async () => {
    if (waBackfillRunning) return;
    setWaBackfillRunning(true);
    setWaBackfillProgress(null);
    try {
      // Fire background job — gateway responds instantly
      await whatsappService.backfillMediaProcessing({ runAll: true, includeFailed: true });
      setStatus('Background backfill started — processing all failed and pending media...');

      // Poll progress every 4 seconds until job finishes
      if (waBackfillPollRef.current) clearInterval(waBackfillPollRef.current);
      waBackfillPollRef.current = setInterval(async () => {
        try {
          const prog = await whatsappService.getBackfillProgress();
          setWaBackfillProgress({ processed: prog.progress?.processed ?? 0, total: prog.progress?.total ?? 0 });
          setWaMediaStatus({
            pending: prog.pending ?? 0,
            failed: prog.failed ?? 0,
            inFlight: prog.inFlight ?? 0,
            completed: prog.completed ?? 0,
            processing: prog.processing ?? 0,
            byOperator: prog.byOperator,
          });
          if (!prog.progress?.running) {
            clearInterval(waBackfillPollRef.current!);
            waBackfillPollRef.current = null;
            setWaBackfillRunning(false);
            setStatus(`Backfill complete — ${prog.progress?.processed ?? 0} item(s) processed. Completed: ${prog.completed ?? 0}, Failed: ${prog.failed ?? 0}.`);
          }
        } catch {
          // ignore poll errors, keep polling
        }
      }, 4000);
    } catch (error: any) {
      setWaBackfillRunning(false);
      setStatus(error?.response?.data?.error || error?.message || 'Failed to start WhatsApp media backfill.');
    }
  };

  const saveWhatsAppTransferSettings = async () => {
    setWaTransferSaving(true);
    try {
      const nextSettings: AdminSettings = {
        ...settings,
        whatsappTransfer: {
          ...defaultWaTransferSettings,
          ...waTransferSettings,
          autoIntroTemplate: String(waTransferSettings.autoIntroTemplate || '').trim() || defaultWaTransferSettings.autoIntroTemplate,
        },
      };
      const saved = await adminService.saveSettings(nextSettings);
      setSettings(saved);
      setStatus('WhatsApp transfer workflow settings saved.');
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to save WhatsApp transfer workflow settings.');
    } finally {
      setWaTransferSaving(false);
    }
  };

  const saveWhatsAppRecallSettings = async () => {
    setWaRecallSaving(true);
    try {
      const parsedMaxMinutes = Number(waRecallSettings.maxRecallMinutes);
      const maxRecallMinutes = Number.isFinite(parsedMaxMinutes) ? Math.max(1, Math.min(7 * 24 * 60, Math.round(parsedMaxMinutes))) : defaultWaRecallSettings.maxRecallMinutes;
      const nextSettings: AdminSettings = {
        ...settings,
        whatsappRecall: {
          ...defaultWaRecallSettings,
          ...waRecallSettings,
          maxRecallMinutes,
        },
      };
      const saved = await adminService.saveSettings(nextSettings);
      setSettings(saved);
      setStatus('WhatsApp recall policy settings saved.');
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to save WhatsApp recall policy settings.');
    } finally {
      setWaRecallSaving(false);
    }
  };

  const generateOperationsTvMonitorLink = async () => {
    setMonitorBusy(true);
    try {
      const daysRaw = Number(monitorExpiryDays || '30');
      const days = Number.isFinite(daysRaw) ? Math.max(1, Math.min(365, Math.round(daysRaw))) : 30;
      const generated = await adminService.generateOperationsTvPublicToken(days, monitorAlias.trim() || null);
      if (!generated?.token) {
        setStatus('Failed to generate Operations TV monitor token.');
        return;
      }
      const alias = String(generated.alias || '').trim();
      const fullUrl = `${window.location.origin}/meos/operations-tv?token=${encodeURIComponent(generated.token)}&tv=1`;
      const shortUrl = alias
        ? `${window.location.origin}/meos/otv/${encodeURIComponent(alias)}?tv=1`
        : fullUrl;
      setMonitorAlias(alias || monitorAlias.trim());
      setMonitorShortLink(shortUrl);
      setMonitorFullLink(fullUrl);
      setMonitorShowQr(false);
      setStatus('Operations TV monitor link generated.');
      await loadOperationsTvMonitorAccess();
    } finally {
      setMonitorBusy(false);
    }
  };

  const revokeOperationsTvMonitorLink = async () => {
    setMonitorBusy(true);
    try {
      const ok = await adminService.revokeOperationsTvPublicToken();
      if (!ok) {
        setStatus('Failed to revoke Operations TV monitor token.');
        return;
      }
      setMonitorShortLink('');
      setMonitorFullLink('');
      setMonitorShowQr(false);
      setStatus('Operations TV monitor token revoked.');
      await loadOperationsTvMonitorAccess();
    } finally {
      setMonitorBusy(false);
    }
  };

  const copyOperationsTvMonitorLink = async () => {
    if (!monitorShortLink) return;
    try {
      await navigator.clipboard.writeText(monitorShortLink);
      setStatus('Monitor link copied to clipboard.');
    } catch {
      setStatus('Unable to copy link. Please copy manually.');
    }
  };

  const loadCommsTvMonitorAccess = React.useCallback(async () => {
    const data = await adminService.getCommsTvTokenStatus();
    setCommsMonitorAccess(data);
    if (data?.alias) {
      setCommsMonitorAlias(data.alias);
      setCommsMonitorShortLink(`${window.location.origin}/meos/ctv/${encodeURIComponent(data.alias)}?tv=1`);
    }
  }, []);

  const generateCommsTvMonitorLink = async () => {
    setCommsMonitorBusy(true);
    try {
      const daysRaw = Number(commsMonitorExpiryDays || '30');
      const days = Number.isFinite(daysRaw) ? Math.max(1, Math.min(365, Math.round(daysRaw))) : 30;
      const generated = await adminService.generateCommsTvToken(days, commsMonitorAlias.trim() || null);
      if (!generated?.token) {
        setStatus('Failed to generate Reservation Wall monitor token.');
        return;
      }
      const alias = String(generated.alias || '').trim();
      const fullUrl = `${window.location.origin}/meos/comms-tv?token=${encodeURIComponent(generated.token)}&tv=1`;
      const shortUrl = alias
        ? `${window.location.origin}/meos/ctv/${encodeURIComponent(alias)}?tv=1`
        : fullUrl;
      setCommsMonitorAlias(alias || commsMonitorAlias.trim());
      setCommsMonitorShortLink(shortUrl);
      setCommsMonitorFullLink(fullUrl);
      setCommsMonitorShowQr(false);
      setStatus('Reservation Wall monitor link generated.');
      await loadCommsTvMonitorAccess();
    } finally {
      setCommsMonitorBusy(false);
    }
  };

  const revokeCommsTvMonitorLink = async () => {
    setCommsMonitorBusy(true);
    try {
      const ok = await adminService.revokeCommsTvToken();
      if (!ok) {
        setStatus('Failed to revoke Reservation Wall monitor token.');
        return;
      }
      setCommsMonitorShortLink('');
      setCommsMonitorFullLink('');
      setCommsMonitorShowQr(false);
      setStatus('Reservation Wall monitor token revoked.');
      await loadCommsTvMonitorAccess();
    } finally {
      setCommsMonitorBusy(false);
    }
  };

  const copyCommsTvMonitorLink = async () => {
    if (!commsMonitorShortLink) return;
    try {
      await navigator.clipboard.writeText(commsMonitorShortLink);
      setStatus('Reservation Wall link copied to clipboard.');
    } catch {
      setStatus('Unable to copy link. Please copy manually.');
    }
  };

  const roles: Role[] = ['ADMIN', 'CEO', 'GROUND_OPS', 'RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS'];
  const modules: ModuleID[] = [
    'DASHBOARD',
    'OPERATIONS_TV',
    'QUOTER',
    'SCHEDULED',
    'PLANNER',
    'FRMS',
    'CUSTOMERS',
    'CONTACTS',
    'FINANCE',
    'FLEET',
    'FUEL',
    'PILOTS',
    'USERS',
    'ADMIN',
    'ADMIN_TOOLS',
    'OUTBOUND',
    'INBOX',
    'COMMS',
    'AUDIT',
    'STRATEGY',
  ];
  const agentRoles = ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'PILOT', 'FIRST_OFFICER', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'] as const;

  const fallbackAgent = {
    enabled: false,
    advisoryOnly: true,
    advisoryMode: 'PANEL' as const,
    provider: {
      type: 'GEMINI' as const,
      apiKey: '',
      baseUrl: '',
      model: 'gemini-1.5-pro',
    },
    providerConnections: [
      {
        id: 'primary',
        label: 'Primary Provider',
        enabled: true,
        type: 'GEMINI' as const,
        apiKey: '',
        baseUrl: '',
        model: 'gemini-1.5-pro',
      },
    ],
    taskRouting: {
      DRAFT_ASSIST: { connectionId: 'primary' },
      THREAD_SUMMARY: { connectionId: 'primary' },
      VOICE_TRANSCRIPT: { connectionId: 'primary' },
      DOCUMENT_EXTRACTION: { connectionId: 'primary' },
      CLASSIFICATION: { connectionId: 'primary' },
    },
    modules: {
      QUOTER: true,
      SCHEDULED: true,
      PLANNER: true,
      FUEL: true,
      WHATSAPP: true,
    },
    rolesByModule: {
      QUOTER: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
      SCHEDULED: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
      PLANNER: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
      FUEL: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
      WHATSAPP: ['RESERVATIONS', 'FLIGHT_PLANNER', 'DFO', 'CP', 'ACCOUNTS', 'CEO', 'ADMIN', 'GROUND_OPS'],
    },
    enforcement: {
      blockSeverities: ['HIGH'],
      allowOverride: true,
      requireOverrideReason: true,
      overrideRoles: ['ADMIN', 'DFO'],
    },
    enforcementByModule: {},
    ruleToggles: {},
    knowledgeBaseNotesByModule: {},
    guidance: {
      includeChecklist: true,
      includePolicyRefs: true,
      knowledgeBaseNotes: '',
    },
    communications: {
      whatsapp: {
        enabled: true,
        draftGuardian: {
          enabled: true,
          mode: 'WARN' as const,
          blockSeverities: ['HIGH'],
          requireProfessionalTone: true,
          suggestRewrite: true,
          preferredPhrases: [
            'Kindly allow us to check and revert to you shortly.',
            'Thank you for your patience.',
          ],
          forbiddenPhrases: ['let me check', 'i dont know', 'not sure'],
        },
        contextAssistant: {
          enabled: true,
          suggestActions: true,
          allowEstimateLookup: true,
          allowQuoteLookup: true,
          allowCustomerHistoryLookup: true,
        },
        intelligence: {
          enabled: true,
          generateSummary: true,
          extractTasks: true,
          extractReferences: true,
        },
        replyWatcher: {
          enabled: true,
          detectMissedReplies: true,
          detectBrokenPromises: true,
          reminderMinutes: 15,
        },
      },
      email: {
        enabled: false,
        draftGuardian: {
          enabled: false,
          mode: 'ASSIST' as const,
          blockSeverities: ['HIGH'],
          requireProfessionalTone: true,
          suggestRewrite: true,
          preferredPhrases: [],
          forbiddenPhrases: [],
        },
        contextAssistant: {
          enabled: false,
          suggestActions: true,
          allowEstimateLookup: true,
          allowQuoteLookup: true,
          allowCustomerHistoryLookup: true,
        },
        intelligence: {
          enabled: false,
          generateSummary: true,
          extractTasks: true,
          extractReferences: true,
        },
        replyWatcher: {
          enabled: false,
          detectMissedReplies: true,
          detectBrokenPromises: true,
          reminderMinutes: 30,
        },
        replyIntelligence: {
          templates: [],
        },
      },
    },
  };

  const agentSettings = settings.agent || fallbackAgent;
  const setAgentSettings = (next: typeof agentSettings) => {
    const newSettings = { ...settings, agent: next } as AdminSettings;
    setSettings(newSettings);
    const validation = validateAgentTaskRouting(next);
    if (!validation.ok) {
      setStatus(`AI routing not saved: ${validation.invalidRoutes.map(route => route.task).join(', ')} must be mapped to compatible providers.`);
      return;
    }
    setTimeout(() => adminService.saveSettings(newSettings), 1000);
  };

  React.useEffect(() => {
    const rules = agentSettings.customRulesByModule?.[activeAgentModule] || [];
    setCustomRulesJsonDraft(JSON.stringify(rules, null, 2));
    setCustomRulesJsonError(null);
  }, [activeAgentModule, agentSettings.customRulesByModule]);

  React.useEffect(() => {
    const loadHistory = async () => {
      try {
        const history = await adminService.getAgentHistory();
        setAgentHistory(history);
      } catch (error) {
        console.error('[AdminTools] Failed to load agent history:', error);
      }
    };
    if (activeTab === 'agent') loadHistory();
  }, [activeTab]);

  const loadOperationsTvMonitorAccess = React.useCallback(async () => {
    const data = await adminService.getOperationsTvPublicAccess();
    setMonitorAccess(data);
    const alias = String(data?.alias || '').trim();
    setMonitorAlias(alias);
    setMonitorShortLink(alias ? `${window.location.origin}/meos/otv/${encodeURIComponent(alias)}?tv=1` : '');
    setMonitorFullLink('');
    setMonitorShowQr(false);
  }, []);

  React.useEffect(() => {
    if (activeTab === 'monitor') {
      void loadOperationsTvMonitorAccess();
    }
  }, [activeTab, loadOperationsTvMonitorAccess]);
  React.useEffect(() => {
    if (activeTab === 'commsmonitor') {
      void loadCommsTvMonitorAccess();
    }
  }, [activeTab, loadCommsTvMonitorAccess]);

  const updatePermission = (role: Role, module: ModuleID, type: keyof PermissionSet, value: boolean) => {
    const newPerms = { ...settings.permissions };
    if (!newPerms[role]) {
      newPerms[role] = {} as any;
    }
    newPerms[role] = {
      ...newPerms[role],
      [module]: {
        ...newPerms[role][module],
        [type]: value,
      },
    };
    setSettings({ ...settings, permissions: newPerms });
  };

  const toggleAllForRole = (role: Role, enabled: boolean) => {
    const newPerms = { ...settings.permissions };
    const base = newPerms[role] || ({} as any);
    const next = { ...base };
    modules.forEach(module => {
      next[module] = {
        view: enabled,
        create: enabled,
        edit: enabled,
        delete: enabled,
        approve: enabled,
      };
    });
    newPerms[role] = next;
    setSettings({ ...settings, permissions: newPerms });
  };

  const savePermissions = async (next: AdminSettings) => {
    setPermSaving(true);
    setPermSaveStatus('saving');
    try {
      await adminService.saveSettings(next);
      setPermSaveStatus('saved');
      setTimeout(() => setPermSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('[AdminTools] Failed to save permissions:', error);
      setPermSaveStatus('error');
      setTimeout(() => setPermSaveStatus('idle'), 3000);
    } finally {
      setPermSaving(false);
    }
  };

  const saveSettings = async () => {
    if (!backupSettings) return;
    setIsLoading(true);
    try {
      const updated = await backupApi.updateSettings(backupSettings);
      setBackupSettings(updated);
      setSettings({ ...settings, backup: updated });
      setStatus('Backup settings saved.');
      setRemoteStatus(null);
      setRemoteCheckedAt(null);
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to save settings.');
    } finally {
      setIsLoading(false);
    }
  };

  const runBackup = async () => {
    setIsLoading(true);
    try {
      const entry = await backupApi.runBackup('Manual backup');
      setHistory(prev => [entry, ...prev]);
      setStatus('Backup completed successfully.');
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Backup failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const restoreBackup = async () => {
    if (!restoreId || !restoreConfirm) {
      setStatus('Select a backup and type RESTORE to confirm.');
      return;
    }
    setIsLoading(true);
    try {
      await backupApi.restoreBackup(restoreId, restoreConfirm);
      setStatus('Restore started. Check server logs for progress.');
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Restore failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const testRemote = async () => {
    setIsLoading(true);
    try {
      const result = await backupApi.testRemote(cfg.remotePath || '');
      setRemoteStatus(result);
      setRemoteCheckedAt(new Date().toISOString());
      setStatus(result.ok ? 'Remote path verified.' : result.message);
    } catch (error: any) {
      setRemoteStatus({ ok: false, message: error?.response?.data?.error || error?.message || 'Remote path test failed.' });
      setRemoteCheckedAt(new Date().toISOString());
      setStatus(error?.response?.data?.error || error?.message || 'Remote path test failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadBackup = async (entry: BackupEntry) => {
    setIsLoading(true);
    try {
      const blob = await backupApi.downloadBackup(entry.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = entry.id;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Download failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBackup = async (entry: BackupEntry) => {
    const confirmed = window.confirm(`Delete backup ${entry.id}? This removes the backup file and its history row.`);
    if (!confirmed) return;
    setIsLoading(true);
    try {
      await backupApi.deleteBackup(entry.id);
      setHistory(prev => prev.filter(item => item.id !== entry.id));
      if (restoreId === entry.id) setRestoreId('');
      setStatus('Backup deleted.');
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Delete failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshWhatsAppSessions = React.useCallback(async (
    options?: { silent?: boolean }
  ): Promise<{ sessionItems: WhatsAppSessionItem[]; numberItems: WhatsAppNumberItem[] }> => {
    try {
      const [sessionResult, numberResult] = await Promise.all([
        whatsappService.getSessions(),
        whatsappService.listNumbers(),
      ]);
      const sessionItems = Array.isArray(sessionResult.items) ? sessionResult.items : [];
      const numberItems = Array.isArray(numberResult.items) ? numberResult.items : [];
      setWaSessions(sessionItems);
      setWaNumbers(numberItems);
      return { sessionItems, numberItems };
    } catch (error: any) {
      if (!options?.silent) {
        setStatus(error?.response?.data?.error || error?.message || 'Failed to load WhatsApp sessions.');
      }
      return { sessionItems: [], numberItems: [] };
    }
  }, []);

  React.useEffect(() => {
    if (activeTab !== 'whatsapp') return;
    const timer = setInterval(() => {
      void refreshWhatsAppSessions({ silent: true });
    }, 15000);
    return () => clearInterval(timer);
  }, [activeTab, refreshWhatsAppSessions]);

  const resetWhatsAppForm = () => {
    setWaEditingId('');
    setWaForm({
      ownerUserId: '',
      phoneNumber: '',
      jid: '',
      label: '',
      sessionKey: '',
      status: 'PAUSED',
      automation: {
        transferAfterMinutes: '',
        transferToUserId: '',
        notifyAfterMinutes: '',
        notifyUserId: '',
      },
    });
  };

  const saveWhatsAppNumber = async () => {
    if (!waForm.ownerUserId || !waForm.phoneNumber.trim() || !waForm.jid.trim() || !waForm.sessionKey.trim()) {
      setStatus('Owner, phone number, JID, and session key are required.');
      return;
    }
    setIsLoading(true);
    try {
      if (waEditingId) {
        await whatsappService.updateNumber(waEditingId, {
          ...waForm,
          automation: {
            transferAfterMinutes: waForm.automation.transferAfterMinutes ? Number(waForm.automation.transferAfterMinutes) : null,
            transferToUserId: waForm.automation.transferToUserId || null,
            notifyAfterMinutes: waForm.automation.notifyAfterMinutes ? Number(waForm.automation.notifyAfterMinutes) : null,
            notifyUserId: waForm.automation.notifyUserId || null,
          },
        });
        setStatus('WhatsApp number updated.');
      } else {
        await whatsappService.createNumber({
          ...waForm,
          automation: {
            transferAfterMinutes: waForm.automation.transferAfterMinutes ? Number(waForm.automation.transferAfterMinutes) : null,
            transferToUserId: waForm.automation.transferToUserId || null,
            notifyAfterMinutes: waForm.automation.notifyAfterMinutes ? Number(waForm.automation.notifyAfterMinutes) : null,
            notifyUserId: waForm.automation.notifyUserId || null,
          },
        });
        setStatus('WhatsApp number created.');
      }
      resetWhatsAppForm();
      await refreshWhatsAppSessions();
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to save WhatsApp number.');
    } finally {
      setIsLoading(false);
    }
  };

  const connectWhatsAppNumber = async () => {
    if (!waNumberId.trim()) {
      setStatus('Enter WhatsApp number id before connecting.');
      return;
    }
    setIsLoading(true);
    try {
      const selected = waNumbers.find(item => item.id === waNumberId.trim()) || null;
      await whatsappService.connectNumber(waNumberId.trim(), {
        forceRelink: String(selected?.status || '').toUpperCase() === 'NEEDS_RELINK',
      });
      const qr = await whatsappService.getQrCode(waNumberId.trim()).catch(() => ({ qrCode: null }));
      setWaQrCode(qr.qrCode || null);
      const refreshed = await refreshWhatsAppSessions();
      const currentNumber = refreshed.numberItems.find(item => item.id === waNumberId.trim()) || null;
      if (qr.qrCode) {
        setStatus('WhatsApp connection started. Scan the QR in Linked Devices.');
      } else if (currentNumber?.pairingError) {
        setStatus(`No QR currently available. Last pairing error: ${currentNumber.pairingError}`);
      } else {
        setStatus('WhatsApp connection started. QR not yet generated; click Get QR again in 2-3 seconds.');
      }
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to connect WhatsApp number.');
    } finally {
      setIsLoading(false);
    }
  };

  const connectWhatsAppRow = async (number: WhatsAppNumberItem, forceRelink = false) => {
    setIsLoading(true);
    try {
      await whatsappService.connectNumber(number.id, { forceRelink });
      setWaNumberId(number.id);
      const qr = await whatsappService.getQrCode(number.id).catch(() => ({ qrCode: null }));
      setWaQrCode(qr.qrCode || null);
      const refreshed = await refreshWhatsAppSessions();
      const currentNumber = refreshed.numberItems.find(item => item.id === number.id) || null;
      if (qr.qrCode) {
        setStatus(`QR is ready for ${number.label || number.phoneNumber}.`);
      } else if (currentNumber?.pairingError) {
        setStatus(`No QR yet for ${number.label || number.phoneNumber}. Last pairing error: ${currentNumber.pairingError}`);
      } else {
        setStatus(`Reconnect started for ${number.label || number.phoneNumber}.`);
      }
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || `Failed to reconnect ${number.label || number.phoneNumber}.`);
    } finally {
      setIsLoading(false);
    }
  };

  const getWhatsAppQrForNumber = async (number: WhatsAppNumberItem) => {
    setIsLoading(true);
    try {
      const result = await whatsappService.getQrCode(number.id);
      setWaNumberId(number.id);
      setWaQrCode(result.qrCode || null);
      if (result.qrCode) {
        setStatus(`QR loaded for ${number.label || number.phoneNumber}.`);
      } else {
        setStatus(`No QR available for ${number.label || number.phoneNumber}. Click Reconnect first.`);
      }
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to load WhatsApp QR.');
    } finally {
      setIsLoading(false);
    }
  };

  const disconnectWhatsAppNumber = async () => {
    if (!waNumberId.trim()) {
      setStatus('Enter WhatsApp number id before disconnecting.');
      return;
    }
    setIsLoading(true);
    try {
      await whatsappService.disconnectNumber(waNumberId.trim());
      setWaQrCode(null);
      await refreshWhatsAppSessions();
      setStatus('WhatsApp number disconnected.');
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to disconnect WhatsApp number.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWhatsAppQr = async () => {
    if (!waNumberId.trim()) {
      setStatus('Enter WhatsApp number id before requesting QR.');
      return;
    }
    setIsLoading(true);
    try {
      const result = await whatsappService.getQrCode(waNumberId.trim());
      setWaQrCode(result.qrCode || null);
      const refreshed = await refreshWhatsAppSessions();
      const currentNumber = refreshed.numberItems.find(item => item.id === waNumberId.trim()) || null;
      if (result.qrCode) {
        setStatus('WhatsApp QR loaded.');
      } else if (currentNumber?.pairingError) {
        setStatus(`No QR currently available. Last pairing error: ${currentNumber.pairingError}`);
      } else {
        setStatus('No QR currently available for this number.');
      }
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to load WhatsApp QR.');
    } finally {
      setIsLoading(false);
    }
  };

  const deactivateWhatsAppNumber = async (number: WhatsAppNumberItem) => {
    setIsLoading(true);
    try {
      await whatsappService.deactivateNumber(number.id);
      if (waNumberId === number.id) {
        setWaQrCode(null);
      }
      await refreshWhatsAppSessions();
      setStatus(`WhatsApp number ${number.label || number.phoneNumber} deactivated.`);
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to deactivate WhatsApp number.');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteWhatsAppNumber = async (number: WhatsAppNumberItem) => {
    const label = number.label || number.phoneNumber || number.id;
    const confirmed = window.confirm(`Delete WhatsApp number "${label}"? This also removes its synced threads and messages.`);
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await whatsappService.deleteNumber(number.id);
      if (waEditingId === number.id) {
        resetWhatsAppForm();
      }
      if (waNumberId === number.id) {
        setWaNumberId('');
        setWaQrCode(null);
      }
      await refreshWhatsAppSessions();
      setStatus(`WhatsApp number ${label} deleted.`);
    } catch (error: any) {
      setStatus(error?.response?.data?.error || error?.message || 'Failed to delete WhatsApp number.');
    } finally {
      setIsLoading(false);
    }
  };

  const cfg = backupSettings || {
    enabled: false,
    cron: '0 2 * * *',
    localPath: '/home/blink/MEOS/backups',
    remotePath: '',
    retentionDays: 7,
    encrypt: false,
    encryptionPassphrase: '',
    allowRestore: false,
  };

  const tabMeta: Record<typeof activeTab, { title: string; description: string }> = {
    backup: {
      title: 'Backup & Restore',
      description: 'Protect ME-OS data and restore safely when needed.',
    },
    users: {
      title: 'User Control',
      description: 'Manage user accounts, identities, and access at the admin level.',
    },
    permissions: {
      title: 'Permissions',
      description: 'Control module access and role-based permission boundaries.',
    },
    agent: {
      title: 'Agent',
      description: 'Define governance, rules, and behavioral policy for BLINK AGENT.',
    },
    ai: {
      title: 'AI',
      description: 'Manage LLM connections, API keys, models, and per-task routing.',
    },
    loginpage: {
      title: 'Login Page',
      description: 'Control login-page content, presentation, and public-facing assets.',
    },
    whatsapp: {
      title: 'WhatsApp',
      description: 'Operate WhatsApp sessions, number registry, and media processing.',
    },
    googlecontacts: {
      title: 'Google Contacts',
      description: 'Review Google Contacts sync health, duplicate-risk items, and manual resolution actions.',
    },
    sphone: {
      title: 'S.PHONE',
      description: 'Configure PBX softphone infrastructure and per-operator SIP credentials.',
    },
    tours: {
      title: 'Guided Tours',
      description: 'Create, edit, publish, and manage step-by-step onboarding tours for any page in the system.',
    },
    inbound: {
      title: 'Email Bridge',
      description: 'Monitor the H.BRD inbound bridge health, configure settings, and trigger email backfills after downtime.',
    },
    monitor: {
      title: 'Operations TV Public Monitor',
      description: 'Generate and revoke tokenized public links for wall-monitor read-only access.',
    },
    commsmonitor: {
      title: 'Reservation Wall Public Monitor',
      description: 'Generate and revoke expiring public links for the Reservation Wall TV monitor.',
    },
    other: {
      title: 'Admin Tools',
      description: 'Reserved space for additional admin utilities and maintenance actions.',
    },
  };
  const activeTabMeta = tabMeta[activeTab] || tabMeta.other;

  React.useEffect(() => {
    const applyFromHash = () => {
      const raw = window.location.hash.replace(/^#\//, '');
      const [, queryPart = ''] = raw.split('?');
      const params = new URLSearchParams(queryPart);
      const inboundParam = String(params.get('inboundView') || '').trim().toLowerCase();
      if (inboundParam === 'reply-templates' || inboundParam === 'bridge' || inboundParam === 'gdash') {
        setActiveTab('inbound');
        setInboundView(inboundParam === 'reply-templates' ? 'reply-templates' : inboundParam === 'gdash' ? 'gdash' : 'bridge');
      }
    };
    applyFromHash();
    window.addEventListener('hashchange', applyFromHash);
    return () => window.removeEventListener('hashchange', applyFromHash);
  }, []);

  return (
    <div className="mx-auto w-full max-w-[1200px] px-8 py-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-500 font-semibold">Admin Center</p>
          <h1 className="text-2xl font-bold text-slate-900">{activeTabMeta.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{activeTabMeta.description}</p>
        </div>
        {activeTab === 'backup' && (
          <button
            type="button"
            onClick={saveSettings}
            disabled={isLoading}
            className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Save Backup Settings'}
          </button>
        )}
        {activeTab === 'permissions' && (
          <button
            type="button"
            onClick={() => { void savePermissions(settings); }}
            disabled={permSaving}
            className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {permSaving ? 'Saving...' : 'Save Permission Changes'}
          </button>
        )}
      </div>

      {status && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          {status}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setActiveTab('backup')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'backup'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Backup & Restore
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'users'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          User Control
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('permissions')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'permissions'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Permissions
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('agent')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'agent'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Agent
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('ai')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'ai'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          AI
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('loginpage')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'loginpage'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Login Page
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('whatsapp')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'whatsapp'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          WhatsApp
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('googlecontacts')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'googlecontacts'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Google Contacts
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('sphone')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'sphone'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          S.PHONE
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('tours')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'tours'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Guided Tours
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('inbound')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'inbound'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Email Bridge
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('monitor')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'monitor'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Ops TV Monitor
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('commsmonitor')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'commsmonitor'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Comms TV Monitor
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('other')}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-[0.2em] uppercase ${
            activeTab === 'other'
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 text-slate-500'
          }`}
        >
          Admin Tools
        </button>
      </div>

      {activeTab === 'backup' ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Backup Configuration</h3>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Admin only</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Enabled
              <select
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
                value={cfg.enabled ? 'YES' : 'NO'}
                onChange={e => setBackupSettings({ ...cfg, enabled: e.target.value === 'YES' })}
              >
                <option value="YES">Enabled</option>
                <option value="NO">Disabled</option>
              </select>
            </label>
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Schedule (Cron)
              <input
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={cfg.cron}
                onChange={e => setBackupSettings({ ...cfg, cron: e.target.value })}
              />
            </label>
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Local Backup Path
              <input
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={cfg.localPath}
                onChange={e => setBackupSettings({ ...cfg, localPath: e.target.value })}
              />
            </label>
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              <span className="flex items-center justify-between">
                <span>Mounted NAS/NFS Path (preferred)</span>
                <span
                  className={`rounded-full px-3 py-1 text-[10px] font-semibold tracking-[0.2em] ${
                    remoteStatus
                      ? remoteStatus.ok
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-rose-50 text-rose-700'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {remoteStatus ? (remoteStatus.ok ? 'CONNECTED' : 'OFFLINE') : 'UNCHECKED'}
                </span>
              </span>
              <input
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={cfg.remotePath || ''}
                onChange={e => setBackupSettings({ ...cfg, remotePath: e.target.value })}
                placeholder="/mnt/maos-backups"
              />
              <span className="block text-[10px] normal-case tracking-normal text-slate-400">
                Use the server mount path, not an nfs:// URL. When reachable, backups are written there directly.
              </span>
              {remoteCheckedAt && (
                <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Last check: {new Date(remoteCheckedAt).toLocaleString()}
                </span>
              )}
            </label>
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Retention Days
              <input
                type="number"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={cfg.retentionDays}
                onChange={e => setBackupSettings({ ...cfg, retentionDays: Number(e.target.value) || 0 })}
              />
            </label>
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Encryption
              <select
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
                value={cfg.encrypt ? 'YES' : 'NO'}
                onChange={e => setBackupSettings({ ...cfg, encrypt: e.target.value === 'YES' })}
              >
                <option value="YES">Enabled (AES-256)</option>
                <option value="NO">Disabled</option>
              </select>
            </label>
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Encryption Passphrase
              <input
                type="password"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={cfg.encryptionPassphrase || ''}
                onChange={e => setBackupSettings({ ...cfg, encryptionPassphrase: e.target.value })}
                placeholder="Leave empty to use server env"
              />
            </label>
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Allow Restore
              <select
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
                value={cfg.allowRestore ? 'YES' : 'NO'}
                onChange={e => setBackupSettings({ ...cfg, allowRestore: e.target.value === 'YES' })}
              >
                <option value="NO">Disabled</option>
                <option value="YES">Enabled</option>
              </select>
            </label>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={runBackup}
                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold uppercase tracking-[0.2em]"
                >
                  Run Backup Now
                </button>
                <button
                  type="button"
                  onClick={load}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-xs font-bold uppercase tracking-[0.2em] text-slate-600"
                >
                  Refresh Status
                </button>
                <button
                  type="button"
                  onClick={testRemote}
                  className="px-4 py-2 rounded-xl border border-emerald-200 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700"
                >
                  Test NFS
                </button>
              </div>

              {remoteStatus && (
                <div
                  className={`rounded-xl px-4 py-3 text-xs font-semibold ${
                    remoteStatus.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                  }`}
                >
                  {remoteStatus.message}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
              <h3 className="text-sm font-semibold text-slate-900">Restore</h3>
              <p className="text-xs text-slate-500">
                Restoring overwrites the live database. Use only if you are certain. Requires <code>ALLOW_BACKUP_RESTORE=true</code> server flag.
              </p>
              <select
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={restoreId}
                onChange={e => setRestoreId(e.target.value)}
              >
                <option value="">Select backup file</option>
                {history.map(entry => (
                  <option key={entry.id} value={entry.id}>
                    {new Date(entry.createdAt).toLocaleString()} · {entry.encrypted ? 'ENC' : 'RAW'} · {formatBytes(entry.sizeBytes)}
                  </option>
                ))}
              </select>
              <input
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                placeholder="Type RESTORE to confirm"
                value={restoreConfirm}
                onChange={e => setRestoreConfirm(e.target.value)}
              />
              <button
                type="button"
                onClick={restoreBackup}
                className="w-full px-4 py-3 rounded-xl bg-rose-600 text-white text-xs font-bold uppercase tracking-[0.2em]"
              >
                Restore Backup
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Backup History</h3>
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{history.length} files</span>
            </div>
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="text-left py-2">Date</th>
                    <th className="text-left py-2">Size</th>
                    <th className="text-left py-2">Encrypted</th>
                    <th className="text-left py-2">Location</th>
                    <th className="text-left py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map(entry => (
                    <tr key={entry.id}>
                      <td className="py-2 text-slate-600">{new Date(entry.createdAt).toLocaleString()}</td>
                      <td className="py-2 text-slate-600">{formatBytes(entry.sizeBytes)}</td>
                      <td className="py-2 text-slate-600">{entry.encrypted ? 'Yes' : 'No'}</td>
                      <td className="py-2 text-slate-500">{entry.remotePath || entry.filePath}</td>
                      <td className="py-2">
                        <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => downloadBackup(entry)}
                          className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700"
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteBackup(entry)}
                          className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-700"
                        >
                          Delete
                        </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {history.length === 0 && (
                    <tr>
                      <td className="py-6 text-sm text-slate-500" colSpan={5}>
                        No backups recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : activeTab === 'users' ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <UserManager />
        </div>
      ) : activeTab === 'permissions' ? (
        <PermissionsTab
          roles={roles}
          modules={modules}
          settings={settings}
          updatePermission={updatePermission}
          toggleAllForRole={toggleAllForRole}
          saveSettings={savePermissions}
          isSaving={permSaving}
          saveStatus={permSaveStatus}
        />
      ) : activeTab === 'agent' ? (
        <AgentTab
          agentSettings={agentSettings}
          setAgentSettings={setAgentSettings}
          activeAgentModule={activeAgentModule}
          setActiveAgentModule={setActiveAgentModule}
          agentRoles={agentRoles}
          customRuleDraft={customRuleDraft}
          setCustomRuleDraft={setCustomRuleDraft}
          ruleTestResult={ruleTestResult}
          setRuleTestResult={setRuleTestResult}
          customRulesJsonDraft={customRulesJsonDraft}
          setCustomRulesJsonDraft={setCustomRulesJsonDraft}
          customRulesJsonError={customRulesJsonError}
          setCustomRulesJsonError={setCustomRulesJsonError}
          agentHistory={agentHistory}
          onRollbackSnapshot={async (snapshotId: string) => {
            const ok = confirm('Rollback agent settings to this snapshot?');
            if (!ok) return;
            const updated = await adminService.rollbackAgent(snapshotId);
            if (updated) setSettings(updated);
          }}
        />
      ) : activeTab === 'ai' ? (
        <AITab
          agentSettings={agentSettings}
          setAgentSettings={setAgentSettings}
        />
      ) : activeTab === 'loginpage' ? (
        <LoginPageTab
          settings={settings}
          setSettings={setSettings}
          saveSettings={async (s) => { await adminService.saveSettings(s); }}
        />
      ) : activeTab === 'whatsapp' ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">WhatsApp Session Control</h3>
                <p className="mt-1 text-xs text-slate-500">Run connect, disconnect, and QR retrieval for salesperson WhatsApp numbers.</p>
              </div>
              <button type="button" onClick={refreshWhatsAppSessions} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-700">
                Refresh
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
              <select
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={waNumberId}
                onChange={e => setWaNumberId(e.target.value)}
              >
                <option value="">Select WhatsApp number</option>
                {waNumbers.map(number => (
                  <option key={number.id} value={number.id}>
                    {(number.label || number.phoneNumber) + ' - ' + number.ownerName}
                  </option>
                ))}
              </select>
              <button type="button" onClick={connectWhatsAppNumber} className="rounded-xl bg-emerald-600 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white">
                Connect
              </button>
              <button type="button" onClick={fetchWhatsAppQr} className="rounded-xl border border-sky-200 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-sky-700">
                Get QR
              </button>
              <button type="button" onClick={disconnectWhatsAppNumber} className="rounded-xl border border-rose-200 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-rose-700">
                Disconnect
              </button>
            </div>
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="text-left py-2">Number ID</th>
                    <th className="text-left py-2">Owner</th>
                    <th className="text-left py-2">Phone</th>
                    <th className="text-left py-2">JID</th>
                    <th className="text-left py-2">State</th>
                    <th className="text-left py-2">Started</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {waSessions.map(session => (
                    <tr key={session.numberId}>
                      <td className="py-2 text-slate-700 font-semibold">{session.numberId}</td>
                      <td className="py-2 text-slate-500">{waNumbers.find(item => item.id === session.numberId)?.ownerName || '—'}</td>
                      <td className="py-2 text-slate-600">{session.phoneNumber}</td>
                      <td className="py-2 text-slate-500">{session.jid}</td>
                      <td className="py-2 text-slate-600">{session.state}</td>
                      <td className="py-2 text-slate-500">{new Date(session.startedAt).toLocaleString()}</td>
                    </tr>
                  ))}
                  {waSessions.length === 0 && (
                    <tr>
                      <td className="py-6 text-sm text-slate-500" colSpan={5}>
                        No active WhatsApp sessions detected.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">WhatsApp Media Processing</h3>
                <p className="mt-1 text-xs text-slate-500">Backfill transcript and extracted-text indexing for all media — voice notes, videos, images, and documents — stored per operator.</p>
              </div>
              <button type="button" onClick={refreshWhatsAppMediaStatus} disabled={waBackfillRunning} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-700 disabled:opacity-50">
                Refresh
              </button>
            </div>

            {/* Global status tiles */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-green-700">Completed</div>
                <div className="mt-1 text-2xl font-black text-green-900">{waMediaStatus.completed}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Pending</div>
                <div className="mt-1 text-2xl font-black text-slate-900">{waMediaStatus.pending}</div>
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">Processing</div>
                <div className="mt-1 text-2xl font-black text-violet-900">{waMediaStatus.processing}</div>
              </div>
              <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700">In Flight</div>
                <div className="mt-1 text-2xl font-black text-sky-900">{waMediaStatus.inFlight}</div>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">Failed</div>
                <div className="mt-1 text-2xl font-black text-amber-900">{waMediaStatus.failed}</div>
              </div>
            </div>

            {/* Per-operator breakdown */}
            {Array.isArray(waMediaStatus.byOperator) && waMediaStatus.byOperator.length > 0 && (
              <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                {waMediaStatus.byOperator.map((row: any) => (
                  <div key={row.numberId} className="px-4 py-3 hover:bg-slate-50">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-semibold text-slate-800 truncate">{row.ownerName || row.ownerUserId}</span>
                      <span className="text-[10px] text-slate-400 shrink-0">{row.label || row.phoneNumber}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-bold">
                      <span className="text-slate-400">{row.total} total</span>
                      <span className="text-green-700">{row.completed} done</span>
                      {row.pending > 0 && <span className="text-slate-500">{row.pending} pending</span>}
                      {row.processing > 0 && <span className="text-violet-700">{row.processing} active</span>}
                      {row.failed > 0
                        ? <span className="ml-auto text-amber-700 font-black">{row.failed} failed</span>
                        : <span className="ml-auto text-green-600">✓ clean</span>
                      }
                    </div>
                    {row.total > 0 && (
                      <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-green-500 transition-all"
                          style={{ width: `${Math.min(100, Math.round((row.completed / row.total) * 100))}%` }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Backfill controls */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[140px_auto_1fr_1fr] md:items-center">
              <input
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-50"
                value={waBackfillLimit}
                onChange={e => setWaBackfillLimit(e.target.value.replace(/[^\d]/g, ''))}
                placeholder="Limit"
                disabled={waBackfillRunning}
              />
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
                <input
                  type="checkbox"
                  checked={waBackfillIncludeFailed}
                  onChange={e => setWaBackfillIncludeFailed(e.target.checked)}
                  disabled={waBackfillRunning}
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                />
                Retry failed items too
              </label>
              <button
                type="button"
                onClick={runWhatsAppMediaBackfill}
                disabled={waBackfillRunning}
                className="rounded-xl bg-slate-900 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-60"
              >
                {waBackfillRunning ? 'Running...' : 'Run Backfill'}
              </button>
              <button
                type="button"
                onClick={runWhatsAppMediaBackfillAll}
                disabled={waBackfillRunning}
                title="Process ALL pending and failed media in one go — no limit"
                className="rounded-xl border-2 border-emerald-600 bg-emerald-50 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
              >
                {waBackfillRunning ? 'Running...' : 'Clear All Failed'}
              </button>
            </div>
            {waBackfillRunning && (
              <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 space-y-1.5">
                <p className="text-xs text-violet-700 font-semibold animate-pulse">
                  Processing media assets in background...
                </p>
                {waBackfillProgress && waBackfillProgress.total > 0 && (
                  <>
                    <div className="h-2 w-full rounded-full bg-violet-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-violet-500 transition-all duration-500"
                        style={{ width: `${Math.min(100, Math.round((waBackfillProgress.processed / waBackfillProgress.total) * 100))}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-violet-600">{waBackfillProgress.processed} / {waBackfillProgress.total} items</p>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">WhatsApp Transfer Workflow</h3>
                <p className="mt-1 text-xs text-slate-500">Control transfer popups and default intro text for operators when they take over a thread.</p>
              </div>
              <button
                type="button"
                onClick={saveWhatsAppTransferSettings}
                disabled={waTransferSaving}
                className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-60"
              >
                {waTransferSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <label className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <span>Enable transfer workflow</span>
                <input
                  type="checkbox"
                  checked={waTransferSettings.enabled}
                  onChange={e => setWaTransferSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300"
                />
              </label>
              <label className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <span>Show transfer popup in Inbox Hub</span>
                <input
                  type="checkbox"
                  checked={waTransferSettings.popupEnabled}
                  onChange={e => setWaTransferSettings(prev => ({ ...prev, popupEnabled: e.target.checked }))}
                  disabled={!waTransferSettings.enabled}
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                />
              </label>
              <label className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <span>Enable auto intro on “Open & Reply”</span>
                <input
                  type="checkbox"
                  checked={waTransferSettings.autoIntroEnabled}
                  onChange={e => setWaTransferSettings(prev => ({ ...prev, autoIntroEnabled: e.target.checked }))}
                  disabled={!waTransferSettings.enabled}
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                />
              </label>
              <label className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <span>Allow operator to toggle auto intro</span>
                <input
                  type="checkbox"
                  checked={waTransferSettings.allowOperatorOverride}
                  onChange={e => setWaTransferSettings(prev => ({ ...prev, allowOperatorOverride: e.target.checked }))}
                  disabled={!waTransferSettings.enabled}
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                <span className="uppercase tracking-[0.2em] text-slate-400">Auto Intro Template</span>
                <textarea
                  value={waTransferSettings.autoIntroTemplate}
                  onChange={e => setWaTransferSettings(prev => ({ ...prev, autoIntroTemplate: e.target.value }))}
                  disabled={!waTransferSettings.enabled || !waTransferSettings.autoIntroEnabled}
                  rows={3}
                  placeholder="Hi, I'm taking over your request now. I will assist you from here."
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 disabled:bg-slate-100 disabled:text-slate-400"
                />
                <p className="text-[11px] text-slate-400">Use placeholders: <code>{'{{clientName}}'}</code>, <code>{'{{fromName}}'}</code>.</p>
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                <span className="uppercase tracking-[0.2em] text-slate-400">Return To Owner Template</span>
                <textarea
                  value={String(waTransferSettings.returnToOwnerTemplate || '')}
                  onChange={e => setWaTransferSettings(prev => ({ ...prev, returnToOwnerTemplate: e.target.value }))}
                  disabled={!waTransferSettings.enabled || !waTransferSettings.autoIntroEnabled}
                  rows={3}
                  placeholder="Welcome back {{clientName}}, I am now back on your chat and will continue assisting you."
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 disabled:bg-slate-100 disabled:text-slate-400"
                />
              </label>
              <label className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <span>Ask quick feedback before return-to-owner</span>
                <input
                  type="checkbox"
                  checked={Boolean(waTransferSettings.askFeedbackBeforeReturn)}
                  onChange={e => setWaTransferSettings(prev => ({ ...prev, askFeedbackBeforeReturn: e.target.checked }))}
                  disabled={!waTransferSettings.enabled}
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                <span className="uppercase tracking-[0.2em] text-slate-400">Feedback Prompt Template</span>
                <textarea
                  value={String(waTransferSettings.feedbackPromptTemplate || '')}
                  onChange={e => setWaTransferSettings(prev => ({ ...prev, feedbackPromptTemplate: e.target.value }))}
                  disabled={!waTransferSettings.enabled || !waTransferSettings.askFeedbackBeforeReturn}
                  rows={2}
                  placeholder="Before I return you to my colleague, was this support helpful? (Yes/No)"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 disabled:bg-slate-100 disabled:text-slate-400"
                />
              </label>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">WhatsApp Message Recall Policy</h3>
                <p className="mt-1 text-xs text-slate-500">Control whether operators can recall sent messages and for how long.</p>
              </div>
              <button
                type="button"
                onClick={saveWhatsAppRecallSettings}
                disabled={waRecallSaving}
                className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-60"
              >
                {waRecallSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <label className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <span>Enable recall</span>
                <input
                  type="checkbox"
                  checked={Boolean(waRecallSettings.enabled)}
                  onChange={e => setWaRecallSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300"
                />
              </label>
              <label className="space-y-1 text-xs font-semibold text-slate-600">
                <span className="uppercase tracking-[0.2em] text-slate-400">Recall Window (minutes)</span>
                <input
                  type="number"
                  min={1}
                  max={10080}
                  value={String(waRecallSettings.maxRecallMinutes ?? '')}
                  onChange={e => setWaRecallSettings(prev => ({ ...prev, maxRecallMinutes: Number(e.target.value || 0) }))}
                  disabled={!waRecallSettings.enabled}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 disabled:bg-slate-100 disabled:text-slate-400"
                />
              </label>
              <label className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <span>Allow leadership override after window</span>
                <input
                  type="checkbox"
                  checked={Boolean(waRecallSettings.allowLeadershipOverride)}
                  onChange={e => setWaRecallSettings(prev => ({ ...prev, allowLeadershipOverride: e.target.checked }))}
                  disabled={!waRecallSettings.enabled}
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                />
              </label>
              <label className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <span>Require reason for recall</span>
                <input
                  type="checkbox"
                  checked={Boolean(waRecallSettings.requireReason)}
                  onChange={e => setWaRecallSettings(prev => ({ ...prev, requireReason: e.target.checked }))}
                  disabled={!waRecallSettings.enabled}
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                />
              </label>
              <label className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                <span>Allow recall for media messages</span>
                <input
                  type="checkbox"
                  checked={Boolean(waRecallSettings.allowMediaRecall)}
                  onChange={e => setWaRecallSettings(prev => ({ ...prev, allowMediaRecall: e.target.checked }))}
                  disabled={!waRecallSettings.enabled}
                  className="h-4 w-4 rounded border-slate-300 disabled:opacity-50"
                />
              </label>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">WhatsApp Number Registry</h3>
            <p className="text-xs text-slate-500">Create and maintain the salesperson numbers that Inbound Hub will monitor.</p>
            <div className="grid grid-cols-1 gap-3">
              <select
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={waForm.ownerUserId}
                onChange={e => setWaForm(prev => ({ ...prev, ownerUserId: e.target.value }))}
              >
                <option value="">Select owner user</option>
                {waOwners.map(owner => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}{owner.email ? ` (${owner.email})` : ''}
                  </option>
                ))}
              </select>
              <input
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={waForm.phoneNumber}
                onChange={e => setWaForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder="Phone number"
              />
              <input
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={waForm.jid}
                onChange={e => setWaForm(prev => ({ ...prev, jid: e.target.value }))}
                placeholder="JID e.g. 2557xxxxxxx@s.whatsapp.net"
              />
              <input
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={waForm.label}
                onChange={e => setWaForm(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Label"
              />
              <input
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={waForm.sessionKey}
                onChange={e => setWaForm(prev => ({ ...prev, sessionKey: e.target.value }))}
                placeholder="Session key"
              />
              <select
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                value={waForm.status}
                onChange={e => setWaForm(prev => ({ ...prev, status: e.target.value as any }))}
              >
                <option value="PAUSED">Paused</option>
                <option value="ACTIVE">Active</option>
                <option value="DISCONNECTED">Disconnected</option>
                <option value="NEEDS_RELINK">Needs relink</option>
              </select>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-slate-900">Auto Rescue Rules</h4>
                  <p className="mt-1 text-xs text-slate-500">Automatically transfer unanswered chats and notify a backup handler if they still wait too long.</p>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[140px_minmax(0,1fr)]">
                    <input
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                      value={waForm.automation.transferAfterMinutes}
                      onChange={e => setWaForm(prev => ({ ...prev, automation: { ...prev.automation, transferAfterMinutes: e.target.value.replace(/[^\d]/g, '') } }))}
                      placeholder="Minutes"
                    />
                    <select
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                      value={waForm.automation.transferToUserId}
                      onChange={e => setWaForm(prev => ({ ...prev, automation: { ...prev.automation, transferToUserId: e.target.value } }))}
                    >
                      <option value="">After X min, transfer to...</option>
                      {waOwners.map(owner => (
                        <option key={owner.id} value={owner.id}>
                          {owner.name}{owner.email ? ` (${owner.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[140px_minmax(0,1fr)]">
                    <input
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                      value={waForm.automation.notifyAfterMinutes}
                      onChange={e => setWaForm(prev => ({ ...prev, automation: { ...prev.automation, notifyAfterMinutes: e.target.value.replace(/[^\d]/g, '') } }))}
                      placeholder="Minutes"
                    />
                    <select
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
                      value={waForm.automation.notifyUserId}
                      onChange={e => setWaForm(prev => ({ ...prev, automation: { ...prev.automation, notifyUserId: e.target.value } }))}
                    >
                      <option value="">If still unanswered, notify...</option>
                      {waOwners.map(owner => (
                        <option key={owner.id} value={owner.id}>
                          {owner.name}{owner.email ? ` (${owner.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={saveWhatsAppNumber} className="rounded-xl bg-slate-900 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-white">
                  {waEditingId ? 'Update Number' : 'Create Number'}
                </button>
                <button type="button" onClick={resetWhatsAppForm} className="rounded-xl border border-slate-200 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-700">
                  Clear
                </button>
              </div>
            </div>
            <div className="max-h-[240px] overflow-auto rounded-2xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="text-left px-3 py-2">Label</th>
                    <th className="text-left px-3 py-2">Owner</th>
                    <th className="text-left px-3 py-2">Status / Health</th>
                    <th className="text-left px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {waNumbers.map(number => (
                    <tr key={number.id}>
                      <td className="px-3 py-2 text-slate-700">{number.label || number.phoneNumber}</td>
                      <td className="px-3 py-2 text-slate-500">{number.ownerName}</td>
                      <td className="px-3 py-2 text-slate-600">
                        <div className="font-semibold">{number.status}</div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          Conn: {String(number.connection || 'UNKNOWN').toUpperCase()}
                        </div>
                        {number.maxAutoReconnectAttempts ? (
                          <div className="text-[11px] text-slate-500">
                            Retry: {number.reconnectAttempt || 0}/{number.maxAutoReconnectAttempts}
                          </div>
                        ) : null}
                        {number.nextRetryAt ? (
                          <div className="text-[11px] text-amber-700">
                            Next retry: {formatDateTime(number.nextRetryAt)}
                          </div>
                        ) : null}
                        {number.lastConnectedAt ? (
                          <div className="text-[11px] text-emerald-700">
                            Last up: {formatDateTime(number.lastConnectedAt)}
                          </div>
                        ) : null}
                        {number.disconnectedAt ? (
                          <div className="text-[11px] text-rose-700">
                            Last down: {formatDateTime(number.disconnectedAt)}
                          </div>
                        ) : null}
                        {number.pairingError ? (
                          <div className="mt-1 max-w-[320px] truncate text-[11px] text-rose-600" title={number.pairingError}>
                            {number.pairingError}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setWaEditingId(number.id);
                            setWaNumberId(number.id);
                            setWaForm({
                              ownerUserId: number.ownerUserId,
                              phoneNumber: number.phoneNumber,
                              jid: number.jid,
                              label: number.label || '',
                              sessionKey: number.sessionKey,
                              status: number.status,
                              automation: {
                                transferAfterMinutes: number.automation?.transferAfterMinutes ? String(number.automation.transferAfterMinutes) : '',
                                transferToUserId: number.automation?.transferToUserId || '',
                                notifyAfterMinutes: number.automation?.notifyAfterMinutes ? String(number.automation.notifyAfterMinutes) : '',
                                notifyUserId: number.automation?.notifyUserId || '',
                              },
                            });
                          }}
                          className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => void deactivateWhatsAppNumber(number)}
                          className="rounded-lg border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-700"
                        >
                          Deactivate
                        </button>
                        <button
                          type="button"
                          onClick={() => void connectWhatsAppRow(number)}
                          className="rounded-lg border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700"
                        >
                          Reconnect
                        </button>
                        <button
                          type="button"
                          onClick={() => void connectWhatsAppRow(number, true)}
                          className="rounded-lg border border-sky-200 px-3 py-1 text-xs font-semibold text-sky-700"
                        >
                          Force Relink
                        </button>
                        <button
                          type="button"
                          onClick={() => void getWhatsAppQrForNumber(number)}
                          className="rounded-lg border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-700"
                        >
                          Get QR
                        </button>
                        <button
                          type="button"
                          onClick={() => void deleteWhatsAppNumber(number)}
                          className="rounded-lg border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-700"
                        >
                          Delete
                        </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {waNumbers.length === 0 && (
                    <tr>
                      <td className="px-3 py-6 text-sm text-slate-500" colSpan={4}>
                        No WhatsApp numbers configured yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <h3 className="text-sm font-semibold text-slate-900">QR / Pairing</h3>
            <p className="text-xs text-slate-500">Select or enter a number id from the registry, then click <code>Get QR</code> after connect.</p>
            {selectedWaNumber ? (
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                <div><span className="font-semibold">Status:</span> {selectedWaNumber.status}</div>
                {selectedWaNumber.connection ? <div><span className="font-semibold">Connection:</span> {selectedWaNumber.connection}</div> : null}
                {selectedWaNumber.pairingError ? <div className="mt-1 text-rose-700"><span className="font-semibold">Last error:</span> {selectedWaNumber.pairingError}</div> : null}
              </div>
            ) : null}
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4">
              {waQrCode ? (
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <div className="rounded-2xl bg-white p-4 shadow-sm">
                      <QRCodeSVG value={waQrCode} size={220} bgColor="#ffffff" fgColor="#0f172a" includeMargin />
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Pairing Payload</p>
                    <p className="mt-2 break-all font-mono text-[11px] text-slate-600">{waQrCode}</p>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-600">No QR loaded yet.</div>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'googlecontacts' ? (
        <GoogleContactsTab />
      ) : activeTab === 'sphone' ? (
        <SPhoneTab
          settings={settings}
          setSettings={setSettings}
          saveSettings={async (s) => {
            await adminService.saveSettings(s);
          }}
        />
      ) : activeTab === 'tours' ? (
        <TourTab
          settings={settings}
          setSettings={setSettings}
          saveSettings={async (s) => { await adminService.saveSettings(s); }}
        />
      ) : activeTab === 'inbound' ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setInboundView('bridge')}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] ${
                inboundView === 'bridge' ? 'bg-slate-900 text-white' : 'border border-slate-200 text-slate-600'
              }`}
            >
              Bridge
            </button>
            <button
              type="button"
              onClick={() => setInboundView('reply-templates')}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] ${
                inboundView === 'reply-templates' ? 'bg-slate-900 text-white' : 'border border-slate-200 text-slate-600'
              }`}
            >
              Reply Templates
            </button>
            <button
              type="button"
              onClick={() => setInboundView('gdash')}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] ${
                inboundView === 'gdash' ? 'bg-slate-900 text-white' : 'border border-slate-200 text-slate-600'
              }`}
            >
              G.DASH
            </button>
          </div>
          {inboundView === 'bridge' ? (
            <InboundBridgeTab
              settings={settings}
              setSettings={setSettings}
              saveSettings={async (s) => { await adminService.saveSettings(s); }}
            />
          ) : inboundView === 'reply-templates' ? (
            <InboundReplyTemplatesTab />
          ) : (
            <GDashTab />
          )}
        </div>
      ) : activeTab === 'monitor' ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Token Expiry (Days)
              <input
                type="number"
                min={1}
                max={365}
                value={monitorExpiryDays}
                onChange={(e) => setMonitorExpiryDays(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
              />
            </label>
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Alias (Optional)
              <input
                type="text"
                value={monitorAlias}
                onChange={(e) => setMonitorAlias(e.target.value)}
                placeholder="main-hangar"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
              />
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { void generateOperationsTvMonitorLink(); }}
                disabled={monitorBusy}
                className="px-4 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-bold uppercase tracking-[0.16em] disabled:opacity-50"
              >
                Generate Link
              </button>
              <button
                type="button"
                onClick={() => { void revokeOperationsTvMonitorLink(); }}
                disabled={monitorBusy}
                className="px-4 py-2.5 rounded-xl border border-rose-200 text-rose-700 text-xs font-bold uppercase tracking-[0.16em] disabled:opacity-50"
              >
                Revoke
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Public Monitor Link</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={monitorShortLink}
                placeholder="Short URL (preferred): /meos/otv/:alias"
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-mono text-slate-700"
              />
              <button
                type="button"
                onClick={() => { void copyOperationsTvMonitorLink(); }}
                disabled={!monitorShortLink}
                className="px-3 py-2 rounded-lg border border-slate-300 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 disabled:opacity-40"
              >
                Copy
              </button>
              <button
                type="button"
                onClick={() => setMonitorShowQr((prev) => !prev)}
                disabled={!monitorShortLink}
                className="px-3 py-2 rounded-lg border border-slate-300 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 disabled:opacity-40"
              >
                QR
              </button>
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={monitorFullLink}
                placeholder="Full token URL (fallback): /meos/operations-tv?token=..."
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-mono text-slate-700"
              />
            </div>
            {monitorShowQr && monitorShortLink ? (
              <div className="pt-2">
                <QRCodeSVG value={monitorShortLink} size={168} bgColor="#ffffff" fgColor="#0f172a" includeMargin />
              </div>
            ) : null}
            <p className="text-[11px] text-slate-500">Use this URL on office TVs: it bypasses login and opens a read-only kiosk board.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Status</p>
              <p className={`mt-1 text-sm font-bold ${monitorAccess?.active ? 'text-emerald-700' : 'text-slate-600'}`}>
                {monitorAccess?.active ? 'Active' : monitorAccess?.hasToken ? 'Inactive' : 'Not configured'}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Expires</p>
              <p className="mt-1 text-sm font-semibold text-slate-700">
                {monitorAccess?.expiresAt ? new Date(monitorAccess.expiresAt).toLocaleString() : '—'}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Active Alias</p>
              <p className="mt-1 text-sm font-semibold text-slate-700">
                {monitorAccess?.alias || '—'}
              </p>
            </div>
          </div>
        </div>
      ) : activeTab === 'commsmonitor' ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Token Expiry (Days)
              <input
                type="number"
                min={1}
                max={365}
                value={commsMonitorExpiryDays}
                onChange={(e) => setCommsMonitorExpiryDays(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
              />
            </label>
            <label className="space-y-2 text-xs uppercase tracking-[0.2em] text-slate-400">
              Alias (Optional)
              <input
                type="text"
                value={commsMonitorAlias}
                onChange={(e) => setCommsMonitorAlias(e.target.value)}
                placeholder="reservation-wall"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700"
              />
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { void generateCommsTvMonitorLink(); }}
                disabled={commsMonitorBusy}
                className="px-4 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-bold uppercase tracking-[0.16em] disabled:opacity-50"
              >
                Generate Link
              </button>
              <button
                type="button"
                onClick={() => { void revokeCommsTvMonitorLink(); }}
                disabled={commsMonitorBusy}
                className="px-4 py-2.5 rounded-xl border border-rose-200 text-rose-700 text-xs font-bold uppercase tracking-[0.16em] disabled:opacity-50"
              >
                Revoke
              </button>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Public Reservation Wall Link</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={commsMonitorShortLink}
                placeholder="Short URL (preferred): /meos/ctv/:alias"
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-mono text-slate-700"
              />
              <button
                type="button"
                onClick={() => { void copyCommsTvMonitorLink(); }}
                disabled={!commsMonitorShortLink}
                className="px-3 py-2 rounded-lg border border-slate-300 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 disabled:opacity-40"
              >
                Copy
              </button>
              <button
                type="button"
                onClick={() => setCommsMonitorShowQr((prev) => !prev)}
                disabled={!commsMonitorShortLink}
                className="px-3 py-2 rounded-lg border border-slate-300 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 disabled:opacity-40"
              >
                QR
              </button>
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={commsMonitorFullLink}
                placeholder="Full token URL (fallback): /meos/comms-tv?token=..."
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-mono text-slate-700"
              />
            </div>
            {commsMonitorShowQr && commsMonitorShortLink ? (
              <div className="pt-2">
                <QRCodeSVG value={commsMonitorShortLink} size={168} bgColor="#ffffff" fgColor="#0f172a" includeMargin />
              </div>
            ) : null}
            <p className="text-[11px] text-slate-500">Use this URL on reservation-office TVs: it bypasses login and opens the read-only Reservation Wall. The link expires automatically based on the selected duration.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Status</p>
              <p className={`mt-1 text-sm font-bold ${commsMonitorAccess?.active ? 'text-emerald-700' : 'text-slate-600'}`}>
                {commsMonitorAccess?.active ? 'Active' : commsMonitorAccess?.hasToken ? 'Inactive' : 'Not configured'}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Expires</p>
              <p className="mt-1 text-sm font-semibold text-slate-700">
                {commsMonitorAccess?.expiresAt ? new Date(commsMonitorAccess.expiresAt).toLocaleString() : '—'}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Active Alias</p>
              <p className="mt-1 text-sm font-semibold text-slate-700">
                {commsMonitorAccess?.alias || '—'}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
          More admin tools will appear here.
        </div>
      )}
    </div>
  );
};

export default AdminTools;
